<template>
  <b-container class="mt-5">
    <b-row class="justify-content-center">
      <b-col cols="12" md="8" lg="6">
        <b-button
          v-if="showTable"
          variant="primary"
          class="mb-3"
          block
          @click="back"
        >
          Back
        </b-button>
      </b-col>
    </b-row>

    <b-row class="justify-content-center">
      <b-col cols="12" md="8" lg="6" v-if="!showTable">
        <b-card header="Select Date Range" header-tag="h4" class="shadow">
          <b-form>
            <!-- Date From -->
            <b-form-group label="Date From" label-for="date-from">
              <b-form-datepicker
                id="date-from"
                v-model="dateFrom"
                placeholder="Select start date"
                :max="dateTo"
              ></b-form-datepicker>
            </b-form-group>

            <!-- Date To -->
            <b-form-group label="Date To" label-for="date-to">
              <b-form-datepicker
                id="date-to"
                v-model="dateTo"
                placeholder="Select end date"
                :min="dateFrom"
              ></b-form-datepicker>
            </b-form-group>

            <!-- Submit Button -->
            <b-button variant="primary" class="mt-3" block @click="submitDates">
              Submit
            </b-button>
          </b-form>
        </b-card>
      </b-col>

      <b-col cols="12" v-if="showTable">
        <b-card :header="tdsHeader" header-tag="h4" class="shadow">
          <!-- Export to CSV Button -->
          <b-button variant="success" class="mb-3" @click="exportToCSV">
            Export to CSV
          </b-button>

          <!-- Table -->
          <b-table
            striped
            hover
            responsive="sm"
            :items="paginatedItems"
            :fields="tableFields"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
          >
          </b-table>

          <b-pagination v-model="currentPage" :total-rows="tableData.length" :per-page="perPage"
          aria-controls="cashbook-table" class="mt-3 justify-content-center"></b-pagination>
        </b-card>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import Swal from "sweetalert2";
import {
  BContainer,
  BRow,
  BCol,
  BCard,
  BForm,
  BFormGroup,
  BFormDatepicker,
  BButton,
  BTable, BPagination,
} from "bootstrap-vue";
import { mapActions } from "vuex";
import { format } from "date-fns";
export default {
  components: {
    BContainer,
    BRow,
    BCol,
    BCard,
    BForm,
    BFormGroup,
    BFormDatepicker,
    BButton,
    BTable,
        BPagination,
  },
  data() {
    return {
      dateFrom: null,
      dateTo: null,
      showTable: false,
      tableData: [],
      currentPage: 1,
      perPage: 5,
      sortBy: "date", // Default sorting column
      sortDesc: true, // Ascending order by default
      tableFields: [
        { key: "vatNumber", label: "VAT Number", sortable: true },
        { key: "brn", label: "BRN", sortable: true },
        { key: "name", label: "Name", sortable: true },
        {
          //key: "date", label: "Date", sortable: true
          key: "date",
          label: "Date",
          sortable: true,
          formatter: (value) => (value ? format(value, "dd-MM-yyyy") : "-"),
        },
        { key: "reference", label: "Reference", sortable: true },
        { key: "total", label: "Total", sortable: true },
        { key: "tax", label: "Tax", sortable: true },
      ],
    };
  },
  computed: {
    tdsHeader() {
      return this.dateFrom && this.dateTo
        ? `VAT Report Data from: ${this.dateFrom} to: ${this.dateTo}`
        : "VAT Report Data";
    },

    paginatedItems() {
            const start = (this.currentPage - 1) * this.perPage;
            const end = start + this.perPage;
            return this.sortedTableData.slice(start, end);
        },

        sortedTableData() {
  const data = [...this.formattedTableData]; // clone array

  if (this.sortBy) {
    data.sort((a, b) => {
      const aVal = a[this.sortBy];
      const bVal = b[this.sortBy];

      // Handle dates, strings, numbers
      if (aVal instanceof Date && bVal instanceof Date) {
        return this.sortDesc
          ? bVal - aVal
          : aVal - bVal;
      }

      // Fallback to string comparison
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return this.sortDesc
          ? bVal.localeCompare(aVal)
          : aVal.localeCompare(bVal);
      }

      // Numbers or other
      return this.sortDesc
        ? (bVal ?? 0) - (aVal ?? 0)
        : (aVal ?? 0) - (bVal ?? 0);
    });
  }

  return data;
},

    formattedTableData() {
      return this.tableData.map((item) => {
        const rawDate = item.date ? new Date(item.date) : null;
      return {
        vatNumber: item.vatNumber ?? "-",
        brn: item.brn ?? "-",
        name: item.name ?? "-",
        reference: item.reference ?? "-",
        // date: item.date ? format(new Date(item.date), "dd-MM-yyyy") : "-",
        date : rawDate,
        total: item.total ?? "-",
        tax: item.tax ?? "-",
      };
    });
    },

  },
  methods: {
    ...mapActions("reportModule", ["getVATReturnReportAction"]),

    async submitDates() {
      if (!this.dateFrom || !this.dateTo) {
        this.$bvToast.toast("Please select both dates.", {
          title: "Error",
          variant: "danger",
          solid: true,
        });
        return;
      }

      try {
        Swal.fire({
          title: "Fetching VAT Report data...",
          text: "Please wait...",
          didOpen: () => {
            Swal.showLoading();
          },
          allowOutsideClick: false,
        });

        const payload = {
          dateFrom: this.dateFrom
            ? this.dateFrom
            : new Date().toISOString().split("T")[0], // Default to today's date if missing
          dateTo: this.dateTo
            ? this.dateTo
            : new Date().toISOString().split("T")[0],
        };

        console.log("API Request Payload:", payload);

        // Call API via Vuex action
        const response = await this.getVATReturnReportAction(payload);
        Swal.close();
        console.log("API Response:", response);

        if (response && response.length > 0) {
          this.tableData = response;
          this.showTable = true;
        } else {
          Swal.fire({
            title: "No Data",
            text: "No data available for the selected period.",
            icon: "warning",
            confirmButtonText: "OK",
          });
        }
      } catch (error) {
        console.error("Error fetching VAT Report data:", error);

        if (
          error.response &&
          error.response.data &&
          error.response.data.errors
        ) {
          const errors = error.response.data.errors;
          let errorMessage = "Validation errors occurred:\n";

          for (const key in errors) {
            if (errors.hasOwnProperty(key)) {
              errorMessage += `${key}: ${errors[key].join(", ")}\n`;
            }
          }

          Swal.fire({
            title: "Validation Error",
            text: errorMessage,
            icon: "error",
            confirmButtonText: "OK",
          });
        } else {
          Swal.fire({
            title: "Error",
            text: "Failed to fetch VAT Report data. Please try again.",
            icon: "error",
            confirmButtonText: "OK",
          });
        }
      }
    },
    back() {
      this.showTable = false;
    },

    exportToCSV() {
      // Generate CSV headers from table fields
      const headers = this.tableFields.map((field) => field.label).join(",");

      // Generate CSV rows from the formattedTableData
      const rows = this.formattedTableData.map((item) =>
        this.tableFields.map((field) => `"${item[field.key] ?? "-"}"`).join(",")
      );

      // Combine headers and rows into a single CSV string
      const csvContent = [headers, ...rows].join("\n");

      // Create a Blob for the CSV content
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });

      // Create a temporary download link
      const link = document.createElement("a");
      const currentDate = new Date().toISOString().slice(0, 10); // Format: YYYY-MM-DD
      link.href = URL.createObjectURL(blob);
      link.download = `Table_Data_${currentDate}.csv`;

      // Trigger the download
      link.click();

      // Revoke the Blob URL to free up memory
      URL.revokeObjectURL(link.href);
    },
  },
  back() {
    this.showTable = false;
  },
};
</script>

<style scoped>
.b-card {
  border-radius: 10px;
  box-shadow: 0px 0px 15px rgba(0, 0, 0, 0.1);
}
</style>
